/**
 * One item: where it is, its label, and everything that ever happened to it.
 *
 * The movement history is the reason this page exists. "Who had it in March" is the
 * question that gets asked, and it is answered by a list nobody can edit — every
 * handover and return is a row, closed out rather than overwritten.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { InventoryLabel } from '@/components/InventoryLabel';
import { InventoryPhoto } from '@/components/InventoryPhoto';
import { Modal } from '@/components/Modal';
import { Badge, Card, ErrorNotice, Field, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, type InventoryStatus } from '@/lib/api';
import { dateShort, dateTime, isoDate } from '@/lib/format';
import { categoryLabel } from '@/lib/inventory';
import { useLabel } from '@/lib/useLabel';
import { Combobox } from '@/components/Combobox';
import { personOptions, siteOptions } from '@/features/access/people';

const STATUS_TONE: Record<InventoryStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'> =
  {
    available: 'success',
    assigned: 'primary',
    maintenance: 'warning',
    lost: 'danger',
    retired: 'neutral',
  };

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    /* Side by side once there is room, stacked before that: a 160px label column on a
       375px screen leaves the value wrapping one word per line. */
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm text-muted sm:w-40 sm:shrink-0">{label}</span>
      <span className="text-sm flex-1">{value}</span>
    </div>
  );
}

export function InventoryDetailPage() {
  const { t } = useI18n();
  const L = useLabel();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const item = useQuery({ queryKey: ['inventory', id], queryFn: () => api.inventoryItem(id) });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });
  const sites = useQuery({ queryKey: ['sites'], queryFn: api.sites });

  const [deleting, setDeleting] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [out, setOut] = useState({ holder: '', dueBackOn: '', notes: '' });
  const [back, setBack] = useState({ status: 'available' as InventoryStatus, notes: '' });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  const checkOut = useMutation({
    mutationFn: () => {
      /*
       * One picker for people and sites, with a prefixed value.
       *
       * Two separate dropdowns would let somebody fill in both, and the server would
       * then have to decide which one wins — a decision nobody should be making.
       */
      const [kind, value] = out.holder.split(':');
      return api.checkOutItem(id, {
        userId: kind === 'user' ? value : undefined,
        siteId: kind === 'site' ? value : undefined,
        dueBackOn: out.dueBackOn || undefined,
        notes: out.notes.trim() || undefined,
      });
    },
    onSuccess: async () => {
      setCheckingOut(false);
      setOut({ holder: '', dueBackOn: '', notes: '' });
      await refresh();
    },
  });

  const checkIn = useMutation({
    mutationFn: () => api.checkInItem(id, { status: back.status, notes: back.notes.trim() || undefined }),
    onSuccess: async () => {
      setCheckingIn(false);
      setBack({ status: 'available', notes: '' });
      await refresh();
    },
  });

  /*
   * Soft, into the same recycle bin as everything else, and the server refuses while
   * the item is out with somebody — deleting it then would lose the only record of who
   * has it. The dialog says so before the button is reachable rather than letting
   * somebody find out from an error.
   */
  const remove = useMutation({
    mutationFn: () => api.deleteInventoryItem(id),
    onSuccess: async () => {
      setDeleting(false);
      await queryClient.invalidateQueries({ queryKey: ['inventory'] });
      navigate('/inventory', { replace: true });
    },
  });

  if (item.isLoading) return <Spinner />;
  if (item.error) return <ErrorNotice error={item.error} />;
  if (!item.data) return null;

  const i = item.data.item;

  return (
    <>
      <PageHeader
        title={i.name}
        subtitle={`${i.code}${i.category ? ` · ${categoryLabel(i.category, t)}` : ''}`}
        actions={
          <>
            <Link to="/inventory" className="btn-secondary">
              {t('inventory.backToList')}
            </Link>
            <button
              type="button"
              className="btn-secondary text-muted hover:text-danger"
              onClick={() => {
                remove.reset();
                setDeleting(true);
              }}
            >
              {t('inventory.delete')}
            </button>
            {i.holder ? (
              <button type="button" className="btn-primary" onClick={() => setCheckingIn(true)}>
                {t('inventory.checkIn')}
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled={i.status === 'retired' || i.status === 'lost'}
                onClick={() => {
                  setOut({ holder: '', dueBackOn: '', notes: '' });
                  setCheckingOut(true);
                }}
              >
                {t('inventory.checkOut')}
              </button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="flex items-center gap-3 mb-1">
              <Badge tone={STATUS_TONE[i.status]}>
                {t(`inventory.status_${i.status}` as 'inventory.status_available')}
              </Badge>
              {i.overdueDays !== null ? (
                <Badge tone="danger">{t('inventory.overdueBy', { count: i.overdueDays })}</Badge>
              ) : null}
            </div>

            {/* A one-line gloss under the badge. "In maintenance" is the one nobody
                guesses right, and a status you have to ask about is one people avoid
                setting — which turns a broken meter back into an available one. */}
            <p className="text-xs text-muted mb-3 leading-relaxed">
              {t(`inventory.statusHint_${i.status}` as 'inventory.statusHint_available')}
            </p>

            <Row
              label={t('inventory.holder')}
              value={
                i.holder ? (
                  <>
                    {i.holder.name}
                    <span className="text-xs text-subtle ms-2">
                      {t(i.holder.kind === 'user' ? 'inventory.holderPerson' : 'inventory.holderSite')}
                    </span>
                  </>
                ) : (
                  t('inventory.inStore')
                )
              }
            />
            <Row
              label={t('inventory.checkedOutAt')}
              value={i.checkedOutAt ? dateTime(i.checkedOutAt) : null}
            />
            <Row label={t('inventory.dueBack')} value={i.dueBackOn ? dateShort(i.dueBackOn) : null} />
            <Row label={t('inventory.serialNumber')} value={i.serialNumber} />
            <Row
              label={t('inventory.manufacturer')}
              value={[i.manufacturer, i.model].filter(Boolean).join(' ') || null}
            />
            <Row label={t('inventory.purchasedOn')} value={i.purchasedOn ? dateShort(i.purchasedOn) : null} />
            <Row
              label={t('inventory.purchasePrice')}
              value={i.purchasePrice === null ? null : `€ ${i.purchasePrice.toFixed(2)}`}
            />
            <Row label={t('inventory.description')} value={i.description} />
            <Row label={t('inventory.externalCode')} value={i.externalCode} />
          </Card>

          <Card>
            <h2 className="font-bold mb-1">{t('inventory.history')}</h2>
            <p className="text-xs text-muted mb-4">{t('inventory.historyHint')}</p>

            {item.data.history.length === 0 ? (
              <p className="text-sm text-muted">{t('inventory.neverIssued')}</p>
            ) : (
              <ul className="space-y-4">
                {item.data.history.map((m) => (
                  <li key={m.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-semibold text-sm">
                        {m.user_name ?? m.site_name ?? '—'}
                      </span>
                      {m.job_reference ? (
                        <span className="text-xs text-subtle">{m.job_reference}</span>
                      ) : null}
                      {m.returned_at ? null : <Badge tone="primary">{t('inventory.stillOut')}</Badge>}
                    </div>

                    <div className="text-xs text-muted tabular mt-1">
                      {t('inventory.outOn', { date: dateTime(m.checked_out_at) })}
                      {m.checked_out_by_name ? ` · ${m.checked_out_by_name}` : ''}
                      {m.returned_at
                        ? ` — ${t('inventory.backOn', { date: dateTime(m.returned_at) })}`
                        : ''}
                    </div>

                    {m.checkout_notes ? (
                      <p className="text-sm mt-1.5 leading-relaxed">{m.checkout_notes}</p>
                    ) : null}
                    {m.return_notes ? (
                      <p className="text-sm mt-1 leading-relaxed text-muted">
                        ↩ {m.return_notes}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          {/* Card and all, or nothing at all. A card whose whole job is to report its
              own emptiness is worse than no card. */}
          <InventoryPhoto
            url={i.photoUrl}
            alt={t('inventory.photoOf', { name: i.name })}
            frame="card overflow-hidden"
            className="w-full aspect-[4/3] object-cover bg-surface-alt"
          />

          <Card>
            <h2 className="font-bold mb-1">{t('inventory.label')}</h2>
            <p className="text-xs text-muted mb-4 leading-relaxed">{t('inventory.labelHint')}</p>

            <div className="print-sheet flex justify-center py-2">
              <InventoryLabel code={i.code} kind={i.codeKind} name={i.name} size={150} />
            </div>

            <button type="button" className="btn-secondary w-full mt-4" onClick={() => window.print()}>
              {t('inventory.print')}
            </button>
          </Card>
        </div>
      </div>

      {/* Reversible for thirty days, so this is a speed bump rather than a wall — but
          an item carries its whole movement history, and that is not something to lose
          on a mis-click. */}
      <Modal
        open={deleting}
        onClose={() => setDeleting(false)}
        size="sm"
        title={t('inventory.deleteTitle')}
        description={t('inventory.deleteConfirm', { code: i.code, name: i.name })}
        footer={
          <>
            <button
              type="button"
              className="btn-danger"
              disabled={remove.isPending || i.holder !== null}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? t('common.saving') : t('common.delete')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setDeleting(false)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        {i.holder ? (
          <p className="text-sm">
            {t('inventory.deleteOutWarning', { holder: i.holder.name ?? '' })}
          </p>
        ) : null}
        {remove.error ? <ErrorNotice error={remove.error} /> : null}
      </Modal>

      {/* --- Hand it over --- */}
      <Modal
        open={checkingOut}
        onClose={() => setCheckingOut(false)}
        title={t('inventory.checkOutTitle', { name: i.name })}
        description={t('inventory.checkOutHint')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!out.holder || checkOut.isPending}
              onClick={() => checkOut.mutate()}
            >
              {checkOut.isPending ? t('common.saving') : t('inventory.checkOut')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCheckingOut(false)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('inventory.giveTo')} hint={t('inventory.giveToHint')}>
            <Combobox
              value={out.holder}
              onChange={(holder) => setOut({ ...out, holder })}
              options={[
                ...personOptions(users.data?.users ?? [], L, t('common.employees')).map((o) => ({ ...o, value: `user:${o.value}` })),
                ...siteOptions(sites.data?.sites ?? [], t('common.sites')).map((o) => ({ ...o, value: `site:${o.value}` })),
              ]}
            />
          </Field>

          <Field label={t('inventory.dueBack')} hint={t('inventory.dueBackHint')}>
            <input
              type="date"
              className="input"
              min={isoDate(new Date())}
              value={out.dueBackOn}
              onChange={(e) => setOut({ ...out, dueBackOn: e.target.value })}
            />
          </Field>

          <Field label={t('inventory.conditionOut')} hint={t('inventory.conditionHint')}>
            <textarea
              className="input h-20 py-2 resize-none"
              value={out.notes}
              onChange={(e) => setOut({ ...out, notes: e.target.value })}
            />
          </Field>

          {checkOut.error ? <ErrorNotice error={checkOut.error} /> : null}
        </div>
      </Modal>

      {/* --- Take it back --- */}
      <Modal
        open={checkingIn}
        onClose={() => setCheckingIn(false)}
        title={t('inventory.checkInTitle', { name: i.name })}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={checkIn.isPending}
              onClick={() => checkIn.mutate()}
            >
              {checkIn.isPending ? t('common.saving') : t('inventory.checkIn')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCheckingIn(false)}>
              {t('common.cancel')}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label={t('inventory.backAs')} hint={t('inventory.backAsHint')}>
            <select
              className="input"
              value={back.status}
              onChange={(e) => setBack({ ...back, status: e.target.value as InventoryStatus })}
            >
              <option value="available">{t('inventory.status_available')}</option>
              <option value="maintenance">{t('inventory.status_maintenance')}</option>
              <option value="lost">{t('inventory.status_lost')}</option>
              <option value="retired">{t('inventory.status_retired')}</option>
            </select>
          </Field>

          <Field label={t('inventory.conditionIn')}>
            <textarea
              className="input h-20 py-2 resize-none"
              value={back.notes}
              onChange={(e) => setBack({ ...back, notes: e.target.value })}
            />
          </Field>

          {checkIn.error ? <ErrorNotice error={checkIn.error} /> : null}
        </div>
      </Modal>
    </>
  );
}
